import { intersection, onlyUnique } from '@go-shared/lib/functional';
import {
  getDayFirstArrival,
  getDayLastDeparture,
  getDayLocation,
  getSailingInitialPort,
} from '@go-shared/lib/sailing';
import {
  DEFAULT_HOUR_FORMAT,
  DEFAULT_TIMESTR_FORMAT,
  getLocaleDateStringUTC,
  dateToIsoUTC,
} from '@go-shared/lib/time';
import { hashStringToColor } from '@go-shared/lib/utils';
import { GroupBooking } from '@go-shared/types/groupBooking';
import {
  BareSimpleSailing,
  BareSimpleSailingDay,
  BareSimpleSailingDayBundle,
} from '@go-shared/types/sailing';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import Fuse from 'fuse.js';
import React, { useEffect, useMemo, useState } from 'react';

import {
  BubbleBadge,
  BubbleButton,
  BubbleCheckbox,
  BubbleFormGroup,
  BubbleInput,
  BubbleTable,
  BubbleTypeahead,
} from '../BubbleUI';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '../BubbleUI/BubbleTable';
import { BubbleTypeaheadOption, BubbleTypeaheadValue } from '../BubbleUI/BubbleTypeahead';
import { FormGenericContainer } from '../FormGenericContainer';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { GroupForm } from './Workflows';
import FormStatusFooter from './FormStatusFooter';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { getSimpleSailingsByProperty } from '@go-client/lib/api/itinerary';

const SUBVIEWS = ['voyage', 'venue'] as const;
type SubView = (typeof SUBVIEWS)[number];

interface SailingTableItem {
  id: string;
  name: string;
  date: string;
  port: string;
  days: number;
  _data: BareSimpleSailing;
}

interface SailingDayTableItem {
  id: string;
  date: string;
  day: string;
  location: string;
  arrival: string;
  departure: string;
  _index: number;
  _sailing: BareSimpleSailing;
  _data: BareSimpleSailingDay;
}

interface VenueTemplateTableItem {
  id: string;
  name: string;
  level: number;
  occupancy: number;
  configurations: string[];
  _data: VenueTemplate;
}

interface SelectItineraryPageProps {
  pageIndex?: number;
  sailings: BareSimpleSailing[];
  venueTemplates: VenueTemplate[];
  isNewGroup: boolean;
  groupForm: GroupForm;
  selectedGroup?: GroupBooking;
  selectedSailingBundle?: BareSimpleSailingDayBundle;
  selectedVenue?: VenueTemplate;
  showDayContext?: boolean;
  defaultStartDate?: string;
  defaultEndDate?: string;
  onClose?: () => void;
  onSelectSailingBundle: (bundle: BareSimpleSailingDayBundle) => void;
  handleOnSelectVenue: (venueTemplate: VenueTemplate) => void;
  onToggleShowDayContext: () => void;
  onGoBack?: () => void;
  onGoNext: () => void;
}

interface ItineraryFilters {
  search: string;
  duration1: boolean;
  duration2: boolean;
  duration3: boolean;
  duration4: boolean;
  departing: Array<BubbleTypeaheadValue<string>>;
  startDate?: string;
  endDate?: string;
}

interface VenueFilters {
  search: string;
  occupancy1: boolean;
  occupancy2: boolean;
  occupancy3: boolean;
  occupancy4: boolean;
  occupancy5: boolean;
  occupancy6: boolean;
  configurations: Array<BubbleTypeaheadValue<string>>;
}

export default function SelectItineraryPage(props: SelectItineraryPageProps) {
  const { activeProperty: property } = useActiveProperty();
  const [currSubView, setCurrSubView] = useState<SubView>(SUBVIEWS[0]);
  const [expandedSailingId, setExpandedSailingId] = useState<string | undefined>(undefined);

  const { activeProperty } = useActiveProperty();

  useEffect(() => {
    setExpandedSailingId(props.selectedSailingBundle?.sailing._id);
  }, [props.selectedSailingBundle]);

  const [showItineraryFilters, setShowItineraryFilters] = useState(false);
  const [itineraryFilters, setItineraryFilters] = useState<ItineraryFilters>({
    search: '',
    duration1: false,
    duration2: false,
    duration3: false,
    duration4: false,
    departing: [],
    startDate: props.defaultStartDate,
    endDate: props.defaultEndDate,
  });

  const [sailings, setSailings] = useState<BareSimpleSailing[]>(props.sailings);
  useEffect(() => {
    if (itineraryFilters.startDate || itineraryFilters.endDate) {
      getSimpleSailingsByProperty(
        property._id,
        itineraryFilters.startDate,
        itineraryFilters.endDate
      ).then(res => setSailings(res));
    }
  }, [itineraryFilters.startDate, itineraryFilters.endDate]);

  const [showVenueFilters, setShowVenueFilters] = useState(false);
  const [venueFilters, setVenueFilters] = useState<VenueFilters>({
    search: '',
    occupancy1: false,
    occupancy2: false,
    occupancy3: false,
    occupancy4: false,
    occupancy5: false,
    occupancy6: false,
    configurations: [],
  });

  function changeView(newView: SubView) {
    setCurrSubView(newView);
  }

  const sailingTableItems: SailingTableItem[] = useMemo(() => {
    return sailings.map(s => ({
      id: s._id,
      name: s.name,
      date: getLocaleDateStringUTC(s.days[0].date),
      port: getSailingInitialPort(s),
      days: s.days.length,
      _data: s,
    }));
  }, [sailings]);

  const itineraryFuse = useMemo(() => {
    return new Fuse(
      sailingTableItems.map(opt => ({
        text: opt.name,
        option: opt,
      })),
      {
        ...DEFAULT_FUSE_SETTINGS,
        keys: ['text'],
      }
    );
  }, [sailingTableItems]);

  const itineraryFuseSearchResults = useMemo(() => {
    if (!itineraryFilters.search) return undefined;
    return itineraryFuse.search(itineraryFilters.search).map(s => s.item.option);
  }, [itineraryFuse, itineraryFilters.search]);

  const filteredSailingTableItems = (itineraryFuseSearchResults || sailingTableItems).filter(
    sti => {
      let hasDurationFilters = false;
      let durationCheck = false;

      if (
        itineraryFilters.duration1 ||
        itineraryFilters.duration2 ||
        itineraryFilters.duration3 ||
        itineraryFilters.duration4
      ) {
        hasDurationFilters = true;
      }

      if (itineraryFilters.duration1 && sti.days >= 1 && sti.days <= 3) {
        durationCheck = true;
      }
      if (itineraryFilters.duration2 && sti.days >= 4 && sti.days <= 7) {
        durationCheck = true;
      }
      if (itineraryFilters.duration3 && sti.days >= 8 && sti.days <= 14) {
        durationCheck = true;
      }
      if (itineraryFilters.duration4 && sti.days >= 15) {
        durationCheck = true;
      }

      const stiLocs: string[] = [];
      sti._data.days.forEach(day =>
        getDayLocation(day)
          .split('/')
          .forEach(d => stiLocs.push(d.trim().toLowerCase()))
      );

      const hasDepartingFilters = itineraryFilters.departing.length > 0;
      const departingCheck =
        intersection(
          stiLocs,
          itineraryFilters.departing.map(c => c.item.trim().toLowerCase())
        ).length > 0;

      const hasDateFilters = itineraryFilters.startDate || itineraryFilters.endDate;
      let dateCheck = true;

      if (itineraryFilters.startDate) {
        dateCheck =
          dateCheck &&
          sti._data.days.findIndex(d => dateToIsoUTC(d.date) >= itineraryFilters.startDate!) > -1;
      }
      if (itineraryFilters.endDate) {
        dateCheck =
          dateCheck &&
          sti._data.days.findIndex(d => dateToIsoUTC(d.date) <= itineraryFilters.endDate!) > -1;
      }

      const meetsDurationFilter = !hasDurationFilters ? true : durationCheck;
      const meetsDepartingFilter = !hasDepartingFilters ? true : departingCheck;
      const meetsDateFilter = !hasDateFilters ? true : dateCheck;

      return meetsDurationFilter && meetsDepartingFilter && meetsDateFilter;
    }
  );

  const sailingTableFields: Array<BubbleTableFields<SailingTableItem>> = [
    {
      key: 'select',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    { key: 'name' },
    { key: 'date' },
    { key: 'port', label: 'departing' },
    { key: 'days' },
  ];

  const sailingTableTemplates: Array<BubbleTableTemplate<SailingTableItem>> = [
    {
      key: 'select',
      render: item => (
        <BubbleButton
          className="p-0-i stretched-link"
          inverse
          variant="navy"
          onClick={() => {
            setExpandedSailingId(expandedSailingId === item.id ? undefined : item.id);
          }}
          icon={expandedSailingId === item.id ? 'chevron-down' : 'chevron-right'}
        />
      ),
    },
  ];

  const dayTableFields: Array<BubbleTableFields<SailingDayTableItem>> = [
    {
      key: 'select',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    { key: 'date' },
    { key: 'day' },
    { key: 'location' },
    { key: 'arrival' },
    { key: 'departure' },
  ];

  const dayTableTemplates: Array<BubbleTableTemplate<SailingDayTableItem>> = [
    {
      key: 'select',
      render: item => (
        <BubbleInput
          type="checkbox"
          appearance="rounded"
          inputClassName="stretched-link bg-white"
          checked={props.selectedSailingBundle?.day._id === item.id}
          onValue={() => {
            if (!props.onSelectSailingBundle) return;
            props.onSelectSailingBundle({
              sailing: item._sailing,
              day: item._data,
              dayIndex: item._index,
              dateIso: dateToIsoUTC(item._data.date),
            });
          }}
        />
      ),
    },
  ];

  const venueTableItems: VenueTemplateTableItem[] = useMemo(
    () =>
      props.venueTemplates
        .filter(vt => !vt.archived && !vt.isPrivate)
        .map(vt => {
          const configs = vt.configurations.map(c => c.name).filter(c => c !== 'default');

          configs.sort();

          return {
            id: vt._id,
            name: vt.name,
            level: vt.level,
            occupancy: vt.occupancy,
            configurations: configs,
            _data: vt,
          };
        }),
    [props.venueTemplates]
  );

  const venueFuse = useMemo(() => {
    return new Fuse(
      venueTableItems.map(opt => ({
        text: opt.name,
        option: opt,
      })),
      {
        ...DEFAULT_FUSE_SETTINGS,
        keys: ['text'],
      }
    );
  }, [venueTableItems]);

  const venueFuseSearchResults = useMemo(() => {
    if (!venueFilters.search) return undefined;
    return venueFuse.search(venueFilters.search).map(s => s.item.option);
  }, [venueFuse, venueFilters.search]);

  const filteredVenueTableItems = (venueFuseSearchResults || venueTableItems).filter(vti => {
    let hasOccupancyFilters = false;
    let occupancyCheck = false;

    if (
      venueFilters.occupancy1 ||
      venueFilters.occupancy2 ||
      venueFilters.occupancy3 ||
      venueFilters.occupancy4 ||
      venueFilters.occupancy5 ||
      venueFilters.occupancy6
    ) {
      hasOccupancyFilters = true;
    }

    if (venueFilters.occupancy1 && vti.occupancy >= 1 && vti.occupancy <= 10) {
      occupancyCheck = true;
    }
    if (venueFilters.occupancy2 && vti.occupancy >= 11 && vti.occupancy <= 25) {
      occupancyCheck = true;
    }
    if (venueFilters.occupancy3 && vti.occupancy >= 26 && vti.occupancy <= 50) {
      occupancyCheck = true;
    }
    if (venueFilters.occupancy4 && vti.occupancy >= 51 && vti.occupancy <= 100) {
      occupancyCheck = true;
    }
    if (venueFilters.occupancy5 && vti.occupancy >= 101 && vti.occupancy <= 200) {
      occupancyCheck = true;
    }
    if (venueFilters.occupancy6 && vti.occupancy >= 201) {
      occupancyCheck = true;
    }

    const hasConfigurationFilters = venueFilters.configurations.length > 0;
    const configurationCheck =
      intersection(
        vti.configurations.map(c => c.trim().toLowerCase()),
        venueFilters.configurations.map(c => c.item.trim().toLowerCase())
      ).length > 0;

    const meetsOccupancyFilter = !hasOccupancyFilters ? true : occupancyCheck;
    const meetsConfigurationFilter = !hasConfigurationFilters ? true : configurationCheck;

    return meetsOccupancyFilter && meetsConfigurationFilter;
  });

  const venueTableFields: Array<BubbleTableFields<VenueTemplateTableItem>> = [
    {
      key: 'select',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    { key: 'name' },
    { key: 'level' },
    { key: 'occupancy' },
    { key: 'configurations' },
  ];

  const venueTableTemplates: Array<BubbleTableTemplate<VenueTemplateTableItem>> = [
    {
      key: 'select',
      render: item => (
        <BubbleInput
          type="checkbox"
          appearance="rounded"
          inputClassName="stretched-link bg-white"
          checked={props.selectedVenue?._id === item.id}
          onValue={() => {
            if (!props.handleOnSelectVenue) return;
            props.handleOnSelectVenue(item._data);
          }}
        />
      ),
    },
    {
      key: 'configurations',
      render: item =>
        item.configurations.length > 0 ? (
          <>
            {item.configurations.map((c, i) => (
              <BubbleBadge key={i} className="mr-1 mt-1" color={hashStringToColor(c)}>
                {c}
              </BubbleBadge>
            ))}
          </>
        ) : undefined,
    },
  ];

  // const allPorts = props.sailings.map(s => getSailingInitialPort(s)).filter(onlyUnique);
  let allPorts: string[] = [];
  sailings.forEach(sail => {
    sail.days.forEach(day =>
      getDayLocation(day)
        .split('/')
        .forEach(n => allPorts.push(n))
    );
  });
  allPorts = allPorts.filter(onlyUnique);
  const portOpts: Array<BubbleTypeaheadOption<string>> = allPorts.map(p => ({
    key: p,
    item: p,
  }));

  const allConfigurations = props.venueTemplates
    .map(vt => vt.configurations.map(c => c.name))
    .flat()
    .filter(onlyUnique)
    .filter(c => c.trim().toLowerCase() !== 'default')
    .map(k => ({
      key: k,
      item: k,
    }));

  return (
    <div className="tflf-2">
      <FormGenericContainer
        footer={() => (
          <>
            <FormStatusFooter {...props} className="text-slate-dark mr-auto" />
            {props.onGoBack && (
              <BubbleButton variant="navy" inverse onClick={props.onGoBack}>
                Go Back
              </BubbleButton>
            )}
            <BubbleButton
              disabled={!props.selectedSailingBundle || !props.selectedVenue}
              variant="navy"
              onClick={props.onGoNext}
            >
              {`Next: Enter Group Booking Details${props.isNewGroup ? ' (New Group)' : ''}`}
            </BubbleButton>
          </>
        )}
      >
        <div className="flex items-start mb-5">
          <div>
            <div className="flex items-center">
              <h1 className="text-lg text-slate-dark font-semibold">
                <span>Create Group Booking</span>
                {props.isNewGroup && <span className="text-crimson"> (New Group)</span>}
              </h1>
              <BubbleBadge color={hashStringToColor(activeProperty.name)} className="ml-3">
                {activeProperty.name}
              </BubbleBadge>
            </div>
            <h2 className="text-2xl text-navy">
              {props.pageIndex !== undefined ? `${props.pageIndex}. ` : ''}Select Voyage & Venue
            </h2>
          </div>
          <div className="ml-auto" />
          {/* <BubbleButton
            className="ml-auto mr-2"
            variant="banana"
            onClick={props.onToggleShowDayContext}
            icon={['fass', 'chart-tree-map']}
          >
            Toggle Venue Context
          </BubbleButton> */}
          <BubbleButton
            variant="navy"
            inverse
            onClick={props.onClose}
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        <div className="flex sticky top-0 z-10 -mx-8" style={{ height: '45px' }}>
          <BubbleButton
            className={`h-full ${currSubView === 'voyage' ? '' : 'font-normal-i'}`}
            appearance="squared"
            variant={currSubView === 'voyage' ? 'navy' : 'smoke'}
            block
            onClick={() => changeView('voyage')}
            icon={props.selectedSailingBundle ? 'check' : 'question'}
          >
            Voyage
          </BubbleButton>
          <BubbleButton
            className={`h-full ${currSubView === 'venue' ? '' : 'font-normal-i'}`}
            appearance="squared"
            variant={currSubView === 'venue' ? 'navy' : 'smoke'}
            block
            onClick={() => changeView('venue')}
            icon={props.selectedVenue ? 'check' : 'question'}
          >
            Venue
          </BubbleButton>
        </div>
        {
          {
            voyage: (
              <BubbleTable
                headVariant="navy"
                responsive
                striped
                stickyHeader={45}
                items={filteredSailingTableItems}
                fields={sailingTableFields}
                templates={sailingTableTemplates}
                detailsKey={expandedSailingId}
                detailsClassName="p-0-i"
                detailsRender={item => {
                  const dayTableItems: SailingDayTableItem[] = item._data.days.map((d, i) => ({
                    id: d._id,
                    date: getLocaleDateStringUTC(d.date),
                    day: d.name,
                    location: getDayLocation(d),
                    arrival: getDayFirstArrival(d, DEFAULT_HOUR_FORMAT, DEFAULT_TIMESTR_FORMAT),
                    departure: getDayLastDeparture(d, DEFAULT_HOUR_FORMAT, DEFAULT_TIMESTR_FORMAT),
                    _sailing: item._data,
                    _index: i,
                    _data: d,
                  }));

                  return (
                    <div className="pb-5">
                      <BubbleTable
                        headVariant="slate"
                        responsive
                        striped
                        items={dayTableItems}
                        fields={dayTableFields}
                        templates={dayTableTemplates}
                      />
                    </div>
                  );
                }}
                showPretitle
                pretitleClassName="p-0-i"
                pretitleRender={() => (
                  <div>
                    <div className="-mx-8 bg-eggshell" style={{ height: '1.5rem' }} />
                    <div className="px-4 pt-4 mb-1 flex items-center">
                      <BubbleInput
                        variant="navy"
                        block
                        type="text"
                        value={itineraryFilters.search}
                        placeholder="Search..."
                        className="flex-grow"
                        onValue={search => setItineraryFilters({ ...itineraryFilters, search })}
                      />
                      <BubbleButton
                        variant="white"
                        inverse
                        className="-mr-4"
                        onClick={() => setShowItineraryFilters(!showItineraryFilters)}
                        iconSuffix={showItineraryFilters ? 'chevron-up' : 'chevron-down'}
                        iconClasses="mr-1"
                        icon="filter"
                      />
                    </div>
                    {showItineraryFilters && (
                      <div className="bg-navy-dark p-4 text-white font-normal normal-case mt-3">
                        <div className="grid gap-4">
                          <BubbleFormGroup label="Days" labelClassName="text-white-i">
                            <div className="grid grid-cols-4 gap-4">
                              <BubbleCheckbox
                                variant="navy"
                                checked={itineraryFilters.duration1}
                                onValue={checked =>
                                  setItineraryFilters({
                                    ...itineraryFilters,
                                    duration1: checked,
                                  })
                                }
                                label="1-3 days"
                              />
                              <BubbleCheckbox
                                variant="navy"
                                checked={itineraryFilters.duration2}
                                onValue={checked =>
                                  setItineraryFilters({
                                    ...itineraryFilters,
                                    duration2: checked,
                                  })
                                }
                                label="4-7 days"
                              />
                              <BubbleCheckbox
                                variant="navy"
                                checked={itineraryFilters.duration3}
                                onValue={checked =>
                                  setItineraryFilters({
                                    ...itineraryFilters,
                                    duration3: checked,
                                  })
                                }
                                label="8-14 days"
                              />
                              <BubbleCheckbox
                                variant="navy"
                                checked={itineraryFilters.duration4}
                                onValue={checked =>
                                  setItineraryFilters({
                                    ...itineraryFilters,
                                    duration4: checked,
                                  })
                                }
                                label="15+ days"
                              />
                            </div>
                          </BubbleFormGroup>
                          <BubbleFormGroup label="Location" labelClassName="text-white-i">
                            <BubbleTypeahead
                              block
                              placeholder="Filter locations shown..."
                              menuClassName="max-h-80 overflow-y-auto"
                              variant="navy"
                              value={itineraryFilters.departing}
                              options={portOpts}
                              labelExtractor={l => l}
                              onAdd={p =>
                                setItineraryFilters({
                                  ...itineraryFilters,
                                  departing: [...itineraryFilters.departing, p],
                                })
                              }
                              onRemove={p =>
                                setItineraryFilters({
                                  ...itineraryFilters,
                                  departing: itineraryFilters.departing.filter(
                                    pp => pp.key !== p.key
                                  ),
                                })
                              }
                            />
                          </BubbleFormGroup>
                          <BubbleFormGroup label="Date Between*" labelClassName="text-white-i">
                            <div className="grid grid-cols-2 gap-4">
                              <BubbleInput
                                type="date"
                                variant="navy"
                                value={itineraryFilters.startDate}
                                block
                                onValue={date =>
                                  setItineraryFilters({ ...itineraryFilters, startDate: date })
                                }
                              />
                              <BubbleInput
                                type="date"
                                variant="navy"
                                value={itineraryFilters.endDate}
                                block
                                onValue={date =>
                                  setItineraryFilters({ ...itineraryFilters, endDate: date })
                                }
                              />
                            </div>
                          </BubbleFormGroup>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              />
            ),
            venue: (
              <BubbleTable
                headVariant="navy"
                stickyHeader={45}
                responsive
                striped
                items={filteredVenueTableItems}
                fields={venueTableFields}
                templates={venueTableTemplates}
                showPretitle
                pretitleClassName="p-0-i"
                pretitleRender={() => (
                  <div>
                    <div className="-mx-8 bg-eggshell" style={{ height: '1.5rem' }} />
                    <div className="px-4 pt-4 mb-1 flex items-center">
                      <BubbleInput
                        variant="navy"
                        block
                        type="text"
                        value={venueFilters.search}
                        placeholder="Search..."
                        inputClassName="outline-none-i"
                        onValue={search => setVenueFilters({ ...venueFilters, search })}
                        className="flex-grow"
                      />
                      <BubbleButton
                        variant="white"
                        inverse
                        className="-mr-4"
                        onClick={() => setShowVenueFilters(!showVenueFilters)}
                        iconSuffix={showVenueFilters ? 'chevron-up' : 'chevron-down'}
                        iconClasses="mr-1"
                        icon="filter"
                      />
                    </div>
                    {showVenueFilters && (
                      <div className="bg-navy-dark p-4 text-white font-normal normal-case mt-3">
                        <div className="grid gap-4">
                          <BubbleFormGroup label="Occupancy" labelClassName="text-white-i">
                            <div className="grid grid-cols-6 gap-4">
                              <BubbleCheckbox
                                variant="navy"
                                checked={venueFilters.occupancy1}
                                onValue={checked =>
                                  setVenueFilters({
                                    ...venueFilters,
                                    occupancy1: checked,
                                  })
                                }
                                label="1-10"
                              />
                              <BubbleCheckbox
                                variant="navy"
                                checked={venueFilters.occupancy2}
                                onValue={checked =>
                                  setVenueFilters({
                                    ...venueFilters,
                                    occupancy2: checked,
                                  })
                                }
                                label="11-25"
                              />
                              <BubbleCheckbox
                                variant="navy"
                                checked={venueFilters.occupancy3}
                                onValue={checked =>
                                  setVenueFilters({
                                    ...venueFilters,
                                    occupancy3: checked,
                                  })
                                }
                                label="26-50"
                              />
                              <BubbleCheckbox
                                variant="navy"
                                checked={venueFilters.occupancy4}
                                onValue={checked =>
                                  setVenueFilters({
                                    ...venueFilters,
                                    occupancy4: checked,
                                  })
                                }
                                label="51-100"
                              />
                              <BubbleCheckbox
                                variant="navy"
                                checked={venueFilters.occupancy5}
                                onValue={checked =>
                                  setVenueFilters({
                                    ...venueFilters,
                                    occupancy5: checked,
                                  })
                                }
                                label="101-200"
                              />
                              <BubbleCheckbox
                                variant="navy"
                                checked={venueFilters.occupancy6}
                                onValue={checked =>
                                  setVenueFilters({
                                    ...venueFilters,
                                    occupancy6: checked,
                                  })
                                }
                                label="201+"
                              />
                            </div>
                          </BubbleFormGroup>
                          <BubbleFormGroup label="Configurations" labelClassName="text-white-i">
                            <BubbleTypeahead
                              block
                              placeholder="Enter configuration name..."
                              menuClassName="max-h-80 overflow-y-auto"
                              options={allConfigurations}
                              variant="navy"
                              value={venueFilters.configurations}
                              labelExtractor={l => l}
                              onAdd={c =>
                                setVenueFilters({
                                  ...venueFilters,
                                  configurations: [...venueFilters.configurations, c],
                                })
                              }
                              onRemove={c =>
                                setVenueFilters({
                                  ...venueFilters,
                                  configurations: venueFilters.configurations.filter(
                                    vfc => vfc.key !== c.key
                                  ),
                                })
                              }
                            />
                          </BubbleFormGroup>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              />
            ),
          }[currSubView]
        }
      </FormGenericContainer>
    </div>
  );
}
